import { useState } from 'react';
import { Check } from 'lucide-react';
import { cn } from '@reflex/ui/lib/utils';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@reflex/ui/components/ui/command';
import { selectableOptions, type FilterDefinition } from './filter-types';

interface FilterOptionListProps {
  definition: FilterDefinition;
  /** Commits a value. The caller closes whatever surface is showing the list. */
  onSelect: (value: string) => void;
  /**
   * Backspace on an empty search box. Wired by the add-filter menu to step back
   * to the facet list — the same reflex that deletes a token in a search input.
   */
  onBack?: () => void;
  className?: string;
}

/**
 * Searchable value picker for a single facet, shared by the chips and by the
 * second step of the add-filter menu so both spell a value the same way.
 */
export function FilterOptionList({
  definition,
  onSelect,
  onBack,
  className,
}: FilterOptionListProps) {
  const [query, setQuery] = useState('');
  const options = selectableOptions(definition);
  const searchLabel = definition.searchPlaceholder ?? `Search ${definition.label.toLowerCase()}…`;

  const filtered =
    definition.withSearch && query.trim()
      ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
      : options;

  return (
    <Command
      shouldFilter={false}
      defaultValue={definition.value}
      label={definition.withSearch ? searchLabel : definition.label}
      className={cn('rounded-none bg-transparent', className)}
    >
      <div className={definition.withSearch ? undefined : 'sr-only'}>
        <CommandInput
          value={query}
          onValueChange={setQuery}
          aria-label={definition.withSearch ? undefined : definition.label}
          placeholder={definition.withSearch ? searchLabel : undefined}
          className="h-auto py-0 text-[13px]"
          onKeyDown={(e) => {
            if (onBack && e.key === 'Backspace' && query === '') {
              e.preventDefault();
              onBack();
            }
          }}
        />
      </div>

      <CommandList className="max-h-none">
        <CommandEmpty className="py-4 text-center text-xs text-muted-foreground">
          No matches
        </CommandEmpty>
        <CommandGroup className="p-[6px] [&_[cmdk-group-items]]:flex [&_[cmdk-group-items]]:flex-col [&_[cmdk-group-items]]:gap-1">
          {filtered.map((option) => {
            const isSelected = option.value === definition.value;
            const isEmpty = option.count === 0;
            return (
              <CommandItem
                key={option.value}
                value={option.value}
                onSelect={() => onSelect(option.value)}
                className={cn(
                  'flex items-center gap-[9px] rounded-[6px] px-2 py-[6px]',
                  isEmpty && 'opacity-50',
                )}
              >
                <div
                  className={cn(
                    'flex size-4 shrink-0 items-center justify-center rounded-[4px] border transition-colors',
                    isSelected ? 'border-primary bg-primary' : 'border-input',
                  )}
                  aria-hidden="true"
                >
                  {isSelected ? (
                    <Check className="size-2.5 text-primary-foreground" aria-hidden="true" />
                  ) : null}
                </div>
                <span className="min-w-0 flex-1 truncate text-[12.5px] text-accent-foreground">
                  {option.label}
                </span>
                {isSelected ? <span className="sr-only">selected</span> : null}
                {option.count !== undefined ? (
                  <span className="shrink-0 text-[11px] text-muted-foreground" aria-hidden="true">
                    {option.count}
                  </span>
                ) : null}
              </CommandItem>
            );
          })}
        </CommandGroup>
      </CommandList>
    </Command>
  );
}
